<template>
  <div
    class="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8"
  >
    <div class="max-w-md w-full space-y-8">
      <!-- Header -->
      <div class="text-center">
        <div
          class="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 dark:bg-blue-900"
        >
          <EnvelopeIcon class="h-8 w-8 text-blue-600 dark:text-blue-400" aria-hidden="true" />
        </div>
        <h2 class="mt-6 text-3xl font-extrabold text-gray-900 dark:text-gray-100">
          Email Verification
        </h2>
        <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
          Verify your email address to complete your account setup
        </p>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center">
        <div class="loading-spinner w-8 h-8 border-blue-600 mx-auto mb-4" />
        <p class="text-gray-600 dark:text-gray-400">Verifying your email...</p>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="text-center">
        <div
          class="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 dark:bg-red-900 mb-4"
        >
          <ExclamationTriangleIcon
            class="h-8 w-8 text-red-600 dark:text-red-400"
            aria-hidden="true"
          />
        </div>
        <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
          Verification Failed
        </h3>
        <p class="text-sm text-gray-600 dark:text-gray-400 mb-6">
          {{ error }}
        </p>
        <div class="space-y-3">
          <button
            class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            @click="retryVerification"
          >
            Try Again
          </button>
          <button
            class="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            @click="goToAuth"
          >
            Go to Login
          </button>
        </div>
      </div>

      <!-- Success State -->
      <div v-else-if="success" class="text-center">
        <div
          class="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 dark:bg-green-900 mb-4"
        >
          <CheckIcon class="h-8 w-8 text-green-600 dark:text-green-400" aria-hidden="true" />
        </div>
        <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
          Email Verified Successfully!
        </h3>
        <p class="text-sm text-gray-600 dark:text-gray-400 mb-6">
          Your email address has been verified. You can now access all features of your account.
        </p>
        <div class="space-y-3">
          <button
            class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            @click="goToDashboard"
          >
            Go to Dashboard
          </button>
          <button
            class="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            @click="goToAuth"
          >
            Go to Login
          </button>
        </div>
      </div>

      <!-- Manual Verification Form -->
      <div v-else class="bg-white dark:bg-gray-800 py-8 px-4 shadow sm:rounded-lg sm:px-10">
        <form class="space-y-6" @submit.prevent="handleManualVerification">
          <div>
            <label for="email" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Email Address
            </label>
            <div class="mt-1">
              <input
                id="email"
                v-model="email"
                name="email"
                type="email"
                autocomplete="email"
                required
                class="appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md placeholder-gray-400 dark:placeholder-gray-500 shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm dark:bg-gray-700 dark:text-gray-100"
                placeholder="Enter your email address"
              />
            </div>
          </div>

          <div>
            <label for="code" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Verification Code
            </label>
            <div class="mt-1">
              <input
                id="code"
                v-model="verificationCode"
                name="code"
                type="text"
                autocomplete="off"
                required
                maxlength="6"
                class="appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md placeholder-gray-400 dark:placeholder-gray-500 shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-center text-lg tracking-widest dark:bg-gray-700 dark:text-gray-100"
                placeholder="Enter 6-digit code"
                @input="formatCode"
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              :disabled="loading || !email || !verificationCode || verificationCode.length !== 6"
              class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              <span v-if="loading" class="flex items-center">
                <svg
                  class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  />
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Verifying...
              </span>
              <span v-else>Verify Email</span>
            </button>
          </div>
        </form>

        <div class="mt-6">
          <div class="relative">
            <div class="absolute inset-0 flex items-center">
              <div class="w-full border-t border-gray-300 dark:border-gray-600" />
            </div>
            <div class="relative flex justify-center text-sm">
              <span class="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                Need help?
              </span>
            </div>
          </div>

          <div class="mt-6 text-center">
            <button
              class="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300"
              @click="goToAuth"
            >
              Contact Support
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { EnvelopeIcon, CheckIcon, ExclamationTriangleIcon } from '@heroicons/vue/24/outline';
import { storageService } from '@/services/storage';
import tierApi from '@/services/tierApi';

const route = useRoute();
const router = useRouter();

// State
const loading = ref(false);
const error = ref('');
const success = ref(false);
const email = ref('');
const verificationCode = ref('');

// Get token and code from URL parameters
const token = computed(() => route.query.token as string);
const code = computed(() => route.query.code as string);

// Methods
const verifyEmailFromUrl = async () => {
  if (!token.value || !code.value) {
    error.value = 'Invalid verification link. Please check your email and try again.';
    return;
  }

  loading.value = true;
  error.value = '';

  try {
    // Ensure tierApi has the auth token
    const authToken = storageService.getAuthToken();
    if (authToken) {
      tierApi.setToken(authToken);
    }

    const response = await tierApi.verifyEmail({
      action: 'verify',
      code: code.value,
      verificationId: token.value,
    });

    if (response.success) {
      success.value = true;

      // Update user verification status if we have an auth store
      // This will be handled by the component that uses this view
    } else {
      error.value = response.error || 'Verification failed. Please check your code and try again.';
    }
  } catch (err: any) {
    console.error('Verification error:', err);

    let errorMessage = 'Verification failed. Please try again.';
    if (err?.response?.data?.error) {
      errorMessage = err.response.data.error;
    } else if (err?.message) {
      errorMessage = err.message;
    }

    error.value = errorMessage;
  } finally {
    loading.value = false;
  }
};

const handleManualVerification = async () => {
  if (!email.value || !verificationCode.value || verificationCode.value.length !== 6) {
    return;
  }

  loading.value = true;
  error.value = '';

  try {
    // Ensure tierApi has the auth token
    const authToken = storageService.getAuthToken();
    if (authToken) {
      tierApi.setToken(authToken);
    }

    const response = await tierApi.verifyEmail({
      action: 'verify',
      code: verificationCode.value,
      verificationId: token.value || '',
      email: email.value,
    });

    if (response.success) {
      success.value = true;
    } else {
      error.value = response.error || 'Verification failed. Please check your code and try again.';
    }
  } catch (err: any) {
    console.error('Manual verification error:', err);

    let errorMessage = 'Verification failed. Please try again.';
    if (err?.response?.data?.error) {
      errorMessage = err.response.data.error;
    } else if (err?.message) {
      errorMessage = err.message;
    }

    error.value = errorMessage;
  } finally {
    loading.value = false;
  }
};

const retryVerification = () => {
  error.value = '';
  if (token.value && code.value) {
    verifyEmailFromUrl();
  }
};

const goToDashboard = () => {
  router.push({ name: 'dashboard' });
};

const goToAuth = () => {
  router.push({ name: 'auth' });
};

const formatCode = (event: Event) => {
  const target = event.target as HTMLInputElement;
  // Remove any non-alphanumeric characters and convert to uppercase
  target.value = target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  verificationCode.value = target.value;
};

// Auto-verify when component mounts if we have token and code
onMounted(async () => {
  if (token.value && code.value) {
    await verifyEmailFromUrl();
  }
});
</script>

<style scoped>
.loading-spinner {
  border: 2px solid #f3f4f6;
  border-top: 2px solid #2563eb;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
